import React, { useState } from 'react';
import { ExbabelLoading } from '@/components/ui/ExbabelLoading';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function LoginPage({ onSuccess, onBack, onSwitchToSignUp, redirectAfter }) {
    const { signInWithEmail, signInWithGoogle, loading, error } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Check for error in query params (e.g. from failed auth links)
    const urlError = searchParams.get('error');
    const [localError, setLocalError] = useState(urlError);

    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setLocalError(null);
        setIsSubmitting(true);

        if (!email || !password) {
            setLocalError('Please enter both email and password');
            setIsSubmitting(false);
            return;
        }

        const result = await signInWithEmail(email, password);

        if (result?.error) {
            setLocalError(result.error.message || 'Login failed');
        } else if (onSuccess) {
            onSuccess();
        }

        setIsSubmitting(false);
    };

    const handleGoogleLogin = async () => {
        setLocalError(null);
        const result = await signInWithGoogle(redirectAfter);

        if (result?.error) {
            setLocalError(result.error.message || 'Google login failed');
        }
    };

    const displayError = localError || error;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                <ExbabelLoading label="Signing you in" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 sm:p-8 font-sans text-gray-900 bg-[#F8FAFC] relative overflow-hidden">
            
            {/* Background Flare */}
            <div className="absolute top-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-48 -left-48 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-[128px] opacity-70"></div>
                <div className="absolute top-48 -right-48 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-[128px] opacity-70"></div>
            </div>

            <div className="w-full max-w-[440px] flex flex-col items-center relative z-10">
                
                {onBack && (
                    <div className="w-full mb-6">
                        <button
                            onClick={onBack}
                            className="text-gray-400 hover:text-gray-900 transition-colors flex items-center text-sm font-medium"
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                            Back
                        </button>
                    </div>
                )}

                {/* Logo */}
                <div className="mb-8">
                    <span className="font-black text-3xl tracking-tight text-[#4F46E5]">
                        Exbabel
                    </span>
                </div>

                <div className="w-full bg-white rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:border sm:border-gray-100 px-6 sm:px-10 py-10">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold tracking-tight text-gray-900 mb-2">Welcome back !</h1>
                        <p className="text-sm text-gray-500">Enter to get unlimited access to translation.</p>
                    </div>

                    {displayError && (
                        <Alert variant="destructive" className="mb-6 rounded-xl">
                            <AlertDescription>{displayError}</AlertDescription>
                        </Alert>
                    )}

                    <form onSubmit={handleEmailLogin} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-sm font-semibold text-gray-700">Email <span className="text-red-500">*</span></Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="Enter your email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isSubmitting}
                                className="h-12 rounded-xl border-gray-200 shadow-sm transition-colors focus-visible:border-indigo-600 focus-visible:ring-1 focus-visible:ring-indigo-600 bg-gray-50/50 text-base sm:text-sm"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-sm font-semibold text-gray-700">Password <span className="text-red-500">*</span></Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    disabled={isSubmitting}
                                    className="h-12 rounded-xl border-gray-200 shadow-sm transition-colors focus-visible:border-indigo-600 focus-visible:ring-1 focus-visible:ring-indigo-600 bg-gray-50/50 pr-12 text-base sm:text-sm"
                                />
                                <button 
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between pt-1">
                            <div className="flex items-center space-x-2">
                                <input 
                                    type="checkbox" 
                                    id="remember" 
                                    className="h-4 w-4 rounded border-gray-300 text-[#4F46E5] focus:ring-[#4F46E5]"
                                />
                                <label htmlFor="remember" className="text-sm font-medium text-gray-700 cursor-pointer">
                                    Remember me
                                </label>
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate('/forgot-password')}
                                className="text-sm text-[#4F46E5] hover:text-indigo-800 font-semibold transition-colors"
                            >
                                Forgot password?
                            </button>
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-12 mt-2 rounded-xl bg-[#4F46E5] text-white hover:bg-indigo-700 transition-colors font-semibold shadow-md shadow-indigo-200/50 text-base"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Logging in...' : 'Log In'}
                        </Button>
                    </form>

                    <div className="relative my-8">
                        <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                            <span className="bg-white px-4 text-gray-400 font-medium">Or, Login with</span>
                        </div>
                    </div>

                    <Button
                        variant="outline"
                        className="w-full h-12 rounded-xl bg-white border-gray-200 hover:bg-gray-50 hover:text-gray-900 font-semibold transition-colors shadow-sm flex items-center justify-center text-base"
                        onClick={handleGoogleLogin}
                        disabled={isSubmitting}
                    >
                        <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        Sign in with Google
                    </Button>
                </div>

                <div className="mt-8">
                    <p className="text-center text-sm text-gray-600 font-medium">
                        Don't have an account?{' '}
                        <button
                            onClick={onSwitchToSignUp}
                            className="text-[#4F46E5] hover:underline font-bold"
                        >
                            Register here
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}
